import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GridMapObject } from './base/grid-feature.base';
import { MapViewport } from './spatial/map-viewport';

/** Place locations accumulated into a grid, weighted by the source's records. */
export interface DensityCellData {
  readonly zoom: number;
  readonly tileX: number;
  readonly tileY: number;
  readonly lat: number;
  readonly lng: number;
  readonly count: number;
}
export interface DensityCell extends DensityCellData, GridMapObject {}

/** The source owns business filters and invalidation; density owns presentation. */
export interface DensitySource {
  readonly densityChanged$: Observable<void>;
  loadDensity(grid: MapViewport): Observable<readonly DensityCellData[]>;
}

export function densityParams(params: HttpParams, grid: MapViewport): HttpParams {
  return params.set('south', grid.south).set('west', grid.west)
    .set('north', grid.north).set('east', grid.east).set('zoom', grid.zoom);
}

const MAX_LATITUDE = 85.05112878;

export const DENSITY_RADIUS = 32;
export const DENSITY_BLUR = 24;

/** Whole cells plus a kernel border keep partial cells from changing on pan.
 * Requests wrap at the date line and adapt resolution to a bounded cell budget. */
export function densityGrids(view: MapViewport, kernelPixels: number): MapViewport[] {
  let zoom = Math.min(22, Math.max(0, Math.floor(view.zoom) + 3));
  for (;;) {
    const n = 2 ** zoom;
    const padding = Math.ceil(kernelPixels / (256 * 2 ** (view.zoom - zoom))) + 1;
    const x = (lng: number) => (lng + 180) / 360 * n;
    const y = (lat: number) => (1 - Math.asinh(Math.tan(
      Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat)) * Math.PI / 180,
    )) / Math.PI) / 2 * n;
    let west = Math.floor(x(view.west)) - padding;
    let east = Math.ceil(x(view.east)) + padding;
    const north = Math.max(0, Math.floor(y(view.north)) - padding);
    const south = Math.min(n, Math.ceil(y(view.south)) + padding);
    if ((Math.min(n, east - west) + 2) * (south - north + 2) > 60000 && zoom > 0) {
      zoom--;
      continue;
    }
    if (east - west >= n) { west = 0; east = n; }
    else { const width = east - west; west = ((west % n) + n) % n; east = west + width; }
    const latitude = (tile: number) => Math.max(-MAX_LATITUDE, Math.min(
      MAX_LATITUDE, Math.atan(Math.sinh(Math.PI * (1 - 2 * tile / n))) * 180 / Math.PI,
    ));
    const bounds = (left: number, right: number): MapViewport => ({
      zoom, west: left / n * 360 - 180, east: right / n * 360 - 180,
      north: latitude(north), south: latitude(south),
    });
    return east > n ? [bounds(west, n), bounds(0, east - n)] : [bounds(west, east)];
  }
}

/** Only visible cells set the automatic scale; the border contributes smooth edges. */
export function densityMaximum(cells: readonly DensityCellData[], view: MapViewport, radius = DENSITY_RADIUS, blur = DENSITY_BLUR): number {
  if (!cells.length) return 1;
  const zoom = cells[0].zoom;
  const n = 2 ** zoom;
  const pixelsPerCell = 256 * 2 ** (view.zoom - zoom);
  const footprint = radius + blur;
  const reach = Math.ceil(footprint / pixelsPerCell);
  // Preserve the default Gaussian estimate while adapting to the rendered footprint.
  const sigma = footprint / 3.5;
  const weights = new Map(cells.map(cell => [cell.tileY * n + cell.tileX, cell.count]));
  const offsets: { x: number; y: number; weight: number }[] = [];
  for (let y = -reach; y <= reach; y++) {
    for (let x = -reach; x <= reach; x++) {
      const distance = (x * x + y * y) * pixelsPerCell * pixelsPerCell;
      if (distance <= footprint ** 2) {
        offsets.push({ x, y, weight: Math.exp(-distance / (2 * sigma * sigma)) });
      }
    }
  }
  const values = cells.filter(cell => {
    const lng = cell.lng + 360 * Math.round(((view.west + view.east) / 2 - cell.lng) / 360);
    return cell.lat >= view.south && cell.lat <= view.north && lng >= view.west && lng <= view.east;
  }).map(cell => offsets.reduce((sum, offset) => sum + offset.weight * (
    weights.get((cell.tileY + offset.y) * n + ((cell.tileX + offset.x) % n + n) % n) ?? 0
  ), 0)).sort((a, b) => a - b);
  // Neighboring mass distinguishes dense neighborhoods of single records from isolated records.
  // A robust high percentile prevents one exceptional address hiding every other hotspot.
  return Math.max(1, values[Math.floor((values.length - 1) * 0.95)] ?? 1);
}
